import React, { useEffect, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import PropTypes from 'prop-types'
import Rellax from 'rellax'
import { Flex, Button, useThemeUI, Box, Image } from 'theme-ui'

import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import RedeemCard from './RedeemCard'
import Heading from '~/components/IntlComponents/Heading'

import Text from '~/components/IntlComponents/Text'
import { GenericSectionDivider } from '~/components/Generic/SectionDivider'
import MacDish from '~/components/Home/MacDish'

const LandingPage = ({ fakeNavigateTo, gifterName, gifterAvatar }) => {
  const { theme, setColorMode } = useThemeUI()
  const themeColours = theme.rawColors.modes['Gift-Flow']
  const translate = useTranslate()
  const rellaxRef = useRef()

  useEffect(() => {
    setColorMode('Gift-Flow')
    new Rellax(rellaxRef.current, { breakpoints: [300, 760, 1060] })
  }, [setColorMode])

  const containerStyle = {
    flexDirection: 'row',
    justifyContent: 'center',
    '@media screen and (max-width: 1200px)': {
      flexDirection: 'column',
    },
  }

  const avatarStyle = {
    alignItems: 'center',
    padding: ['0 20px', '0 80px'],
    maxWidth: '1440px',
    justifyContent: ['center', 'flex-start'],
    my: 40,
    mx: ['auto', 'auto', 0],
    h2: {
      width: 'min-content',
      fontSize: [36, 60],
      maxWidth: ['none', 500],
    },
    img: {
      width: 80,
      height: 80,
      minWidth: 80,
      padding: 1,
      mr: 20,
      borderRadius: '100%',
      objectFit: 'cover',
    },
    '@media screen and (max-width: 1200px)': {
      justifyContent: 'center',
      padding: ['0 20px'],
    },
  }

  const gifterTextStyle = {
    lineHeight: 1,
    color: 'secondary',
    m: 0,
    display: 'inline-table',
  }

  const boxStyle = {
    padding: ['0 40px', '0 80px'],
    position: 'relative',
    color: 'secondary',
    height: ['auto', 560],
    flexDirection: ['column', 'unset'],
    maxWidth: '1440px',
    '@media screen and (max-width: 1200px)': {
      height: 'auto',
      flexDirection: 'column',
    },
  }

  const imageWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40%',
    '@media screen and (max-width: 1200px)': {
      width: '100%',
    },
  }

  const {
    home: { bowlImage, valuePropHeading, valuePropSubheading, valuePropImage },
    landingGiftImage,
  } = useStaticQuery(graphql`
    query macaroniBowlImages {
      home: contentfulHome(slug: { eq: "home" }, node_locale: { eq: "en-US" }) {
        id
        slug
        node_locale
        bowlImage {
          id
          gatsbyImageData(width: 700, quality: 80)
          title
        }
        valuePropHeading
        valuePropSubheading
        valuePropImage {
          valuePropImage {
            title
            gatsbyImageData(width: 500, quality: 80)
          }
        }
      }
      landingGiftImage: contentfulAsset(
        title: { eq: "Gift-Page-Landing-Image" }
      ) {
        id
        title
        gatsbyImageData
        url
      }
    }
  `)

  const multipleGifters = gifterName?.split('')?.includes('&')

  const landingImageURL = landingGiftImage?.url

  const imageStyle = {
    display: 'block',
    maxWidth: ['350px', 'min(50vw,100%)'],
    margin: ['auto', 'unset'],
    zIndex: 1,
  }

  return (
    <>
      <Flex sx={containerStyle}>
        <Box>
          <Flex sx={avatarStyle}>
            <Image src={gifterAvatar || '/images/logo.png'} />
            <Heading as="h2" variant="h2" sx={gifterTextStyle}>
              {gifterName}
            </Heading>
          </Flex>
          <Flex sx={boxStyle}>
            <RedeemCard
              title={
                multipleGifters
                  ? translate('gifting_flow.landing.gift_for_you_hero.titleP')
                  : translate('gifting_flow.landing.gift_for_you_hero.title')
              }
              fakeNavigateTo={fakeNavigateTo}
              gifterName={gifterName}
              gifterAvatar={gifterAvatar}
            >
              <Text sx={{ fontFamily: 'heading', fontSize: 20, mt: 30 }}>
                {multipleGifters
                  ? translate(
                      'gifting_flow.landing.gift_for_you_hero.subtitleP',
                      { gifterName },
                    )
                  : translate(
                      'gifting_flow.landing.gift_for_you_hero.subtitle',
                      { gifterName },
                    )}
              </Text>
              <Text sx={{ mt: 10, mb: 30 }}>
                {translate(
                  'gifting_flow.landing.gift_for_you_hero.description',
                )}
              </Text>
            </RedeemCard>
          </Flex>
        </Box>
        <Box sx={imageWrapperStyle}>
          <Image
            sx={imageStyle}
            src={landingImageURL || '/images/gift_box.png'}
            ref={rellaxRef}
            data-rellax-speed="-2"
            data-rellax-mobile-speed="-1.5"
          />
        </Box>
      </Flex>

      <GenericSectionDivider
        sx={{
          transform: ['scale(70%, 64%) translate(-30%, 34%) rotate(180deg)'],
          width: '200%',
          position: 'relative',
        }}
        fill={themeColours?.secondary}
      />
      <Box
        sx={{
          backgroundColor: 'secondary',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <MacDish
          bowlImage={bowlImage}
          valuePropHeading={valuePropHeading}
          valuePropSubheading={valuePropSubheading}
          valuePropImage={valuePropImage}
          extendedStyles={{
            color: themeColours?.background,
            paddingX: [0, 50],
            pb: 80,
            width: 'fit-content',
            margin: '0 auto',
            textAlign: 'center',
            pt: [350, 'unset'],
            mt: [-400, 'unset'],
          }}
        />
        <Box
          sx={{
            width: ['100%', 'auto'],
            padding: '0 30px',
            marginBottom: ['100px', '150px'],
          }}
        >
          <Button
            data-builder-component="button"
            data-name="gift-redeem-button"
            sx={{ width: ['100%', 'fit-content'] }}
            data-testid={`redeem-button`}
            type="submit"
            variant={'buttons.gift.primary'}
            onClick={() => fakeNavigateTo('form')}
          >
            {translate('gifting_flow.landing.redeem')}
          </Button>
        </Box>
      </Box>
    </>
  )
}

LandingPage.propTypes = {
  fakeNavigateTo: PropTypes.func.isRequired,
  gifterName: PropTypes.string.isRequired,
  gifterAvatar: PropTypes.string,
}

export default LandingPage
